import type { Timestamp } from 'firebase/firestore'

/**
 * The event catalogue: shared central data, written by platform admins and read
 * by everyone. It is what captured timing is eventually identified against.
 *
 * Discovery differs completely by vendor, which is why `source` is a tagged
 * union rather than a common shape. TSL publishes a structured diary and real
 * event IDs; RIS publishes neither, so its events are entered by hand and their
 * addressing can only be discovered close to the event.
 */
export type TimingVendor = 'ris' | 'tsl'

/**
 * RIS runs two independent feeds, and they are not two views of one thing —
 * they were observed serving different events simultaneously. Which feed
 * carries a given race is not knowable in advance.
 */
export type RisFeed = 'json' | 'xml'

export interface RisSource {
  vendor: 'ris'
  feed: RisFeed
  /**
   * Which channels capture should sweep, for the JSON feed. Absent means the
   * Fun Cup set, which is what everything in the catalogue currently is.
   *
   * There is deliberately no per-event address here any more. The JSON feed's
   * "uuid" is a sentinel index into seventeen fixed channels — a bus, not an
   * event identifier — and which bus an event lands on is decided by hand, is
   * not derivable from the series, and is published only on an event page that
   * is routinely stale, sometimes absent, and occasionally wrong. Capture polls
   * the candidates and follows whichever is actually live.
   */
  channels?: string[]
  /**
   * Which channel to believe for each series at this event, by series id.
   *
   * One meeting can carry several championships on several channels at once:
   * at a combined round the French cars are on /funfr, the Belgian cars on
   * /funbe, and the site root shows the whole field together. Which of those
   * a team wants depends on what it is racing for — a team running the French
   * championship wants its position among French cars, a team running a
   * handful of rounds wants the overall screen. That is decided here, by an
   * administrator, once per series, and never asked of the person watching.
   *
   * A series with no entry here is not shown to anybody. There is no default
   * and no inference: both were tried, and both put the same race in the list
   * once per channel that carried it.
   */
  seriesChannels?: Record<string, string>
  /** XML feed only. The path segment, e.g. "funfr" — a timing installation
   *  rather than a series. */
  path?: string
}

export interface TslSource {
  vendor: 'tsl'
  /** TSL's own numeric event ID, straight out of the diary URL. */
  eventId: string
}

export type EventSource = RisSource | TslSource

export interface SeriesDoc {
  name: string
  /** What each vendor's stream calls this series. Matching is on these, not on
   *  the display name, because vendor strings are what actually arrive. */
  vendorNames: Partial<Record<TimingVendor, string[]>>
  createdAt: Timestamp
  updatedAt?: Timestamp
}

export interface EventDoc {
  name: string
  trackId: string
  /**
   * IANA zone, e.g. "Europe/London". Required, and deliberately not inferred
   * from the circuit: timetables are published in wall-clock time, and a
   * circuit does not fix the offset across a DST boundary.
   */
  timezone: string
  startsAt: Timestamp
  endsAt: Timestamp
  source: EventSource
  /**
   * Where the venue publishes refuelling, if it does. Separate from the timing
   * source because it is the circuit's fuel station, not the timekeeper, and
   * the two vary independently. Absent means no fuel is captured.
   */
  fuel?: EventFuel
  createdAt: Timestamp
  updatedAt: Timestamp
}

/**
 * Where fuel fills are read from: the Fun Cup fuel station's site, or a Google
 * Sheet the fuel crew types fills into.
 */
export type FuelSource = 'kronos' | 'sheet'

export const fuelSourceLabels: Record<FuelSource, string> = {
  kronos: 'Kronos (kronos.mtsweb.be)',
  sheet: 'Google Sheet',
}

export interface EventFuel {
  source: FuelSource
  /** For a sheet: any of its URLs. It has to be shared with anyone who has the link. */
  sheetUrl?: string
  /**
   * For a sheet: whose rows are this event's, by its "Type voiture" column —
   * one sheet serves several series, with car numbers in common. FC (Fun Cup)
   * when absent.
   */
  sheetKind?: string
}

/**
 * The sheet the French Fun Cup organisers have kept fills in — Charade in May
 * 2026, cleared and reused for Portimão in October — offered first when a
 * sheet is chosen.
 */
export const knownFuelSheet =
  'https://docs.google.com/spreadsheets/d/1pgePtzx0yZVgCN4LjZguNHuDMUunyoUoib3NKKuqnZE/edit?gid=0'

/** Whether a URL is one of a Google Sheet, which the fuel job can export. */
export function isSheetUrl(url: string): boolean {
  return /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[^/?#]+/.test(url.trim())
}

export type SessionType = 'practice' | 'qualifying' | 'race' | 'other'

export interface SessionDoc {
  eventId: string
  seriesId: string
  name: string
  type: SessionType
  /** Absolute instants. The admin enters wall-clock times and the event's
   *  timezone converts them, so nothing downstream handles zones. */
  scheduledStart: Timestamp
  scheduledEnd: Timestamp
}

/**
 * Whether capture could actually start for this event.
 *
 * RIS JSON events are always capturable now. They used to need a uuid found by
 * hand from the event's own website close to the event, and an event was
 * "not ready" until somebody had. That address turned out not to be an address:
 * it indexes one of seventeen fixed channels, and capture sweeps the candidates
 * rather than being told which. Nothing is left to be missing.
 */
export function isAddressable(source: EventSource): boolean {
  if (source.vendor === 'tsl') return !!source.eventId
  return source.feed === 'json' ? true : !!source.path
}

export function describeSource(source: EventSource): string {
  if (source.vendor === 'tsl') {
    return source.eventId ? `TSL event ${source.eventId}` : 'TSL — no event id'
  }
  if (source.feed === 'json') {
    const channels = source.channels?.length ? source.channels.join(', ') : 'Fun Cup channels'
    return `RIS JSON — ${channels}`
  }
  return source.path ? `RIS XML — /${source.path}/` : 'RIS XML — path not yet known'
}

// ---------------------------------------------------------------- timezones

/**
 * The offset of a zone at a given instant, in milliseconds.
 *
 * Derived by formatting the instant in the target zone and reading the parts
 * back, because there is no direct API for it. Doing this properly matters:
 * timetables are wall-clock, so getting it wrong shifts every session by an
 * hour for half the year.
 */
function offsetMsAt(instant: Date, timeZone: string): number {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts: Record<string, string> = {}
  for (const p of fmt.formatToParts(instant)) {
    if (p.type !== 'literal') parts[p.type] = p.value
  }
  // Intl renders midnight as hour 24 in some locales; normalise it.
  const hour = parts['hour'] === '24' ? '00' : parts['hour']
  const asUtc = Date.UTC(
    Number(parts['year']),
    Number(parts['month']) - 1,
    Number(parts['day']),
    Number(hour),
    Number(parts['minute']),
    Number(parts['second']),
  )
  return asUtc - instant.getTime()
}

/**
 * Converts a wall-clock local time in `timeZone` to the instant it names.
 *
 * `local` is "YYYY-MM-DDTHH:mm" — exactly what a datetime-local input yields.
 *
 * Two passes, because the offset depends on the instant we are trying to find.
 * The first guess uses the offset at the naive-UTC reading; if the corrected
 * instant lands on the other side of a DST transition its offset differs, so we
 * redo it. Without that, times in the hour around a clock change are wrong.
 *
 * Ambiguous local times — the repeated hour when clocks go back, where the same
 * wall clock names two instants — resolve to the LATER of the two. Stated
 * because it is a real property rather than an accident, though it is close to
 * irrelevant here: a race timetable rarely schedules a session inside that hour,
 * and a long race spanning it derives its end from an unambiguous start.
 */
export function wallClockToInstant(local: string, timeZone: string): Date {
  const naive = new Date(`${local}:00Z`)
  const firstOffset = offsetMsAt(naive, timeZone)
  const candidate = new Date(naive.getTime() - firstOffset)
  const secondOffset = offsetMsAt(candidate, timeZone)
  return secondOffset === firstOffset
    ? candidate
    : new Date(naive.getTime() - secondOffset)
}

/** The inverse: renders an instant as "YYYY-MM-DDTHH:mm" wall clock in a zone,
 *  so an edit form shows the times that were entered. */
export function instantToWallClock(instant: Date, timeZone: string): string {
  const shifted = new Date(instant.getTime() + offsetMsAt(instant, timeZone))
  return shifted.toISOString().slice(0, 16)
}

/** Zones the UK-and-Europe events we care about actually use. Free text is
 *  still permitted — this is a convenience, not a constraint. */
export const COMMON_TIMEZONES = [
  'Europe/London',
  'Europe/Brussels',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Lisbon',
  'Europe/Rome',
  'Europe/Berlin',
  'UTC',
] as const

/**
 * The RIS channels, for a picker.
 *
 * timing/internal/ris/channels.go is the authority — it carries the sentinel
 * UUIDs and is what capture actually polls. This is the same closed set with
 * only what a human choosing between them needs, and it is duplicated rather
 * than shared because the alternative is shipping the capture table to a
 * browser to render a dropdown.
 *
 * Seventeen fixed buses, unchanged since the 2026 survey. A channel is a bus
 * and not an event address: it carries whatever session was last pushed to it,
 * and the same bus is reused week after week by different events.
 */
export const risChannels: { id: string; path: string; note: string }[] = [
  { id: 'root', path: '/', note: 'Site root — general or overflow feed' },
  { id: 'moto', path: '/moto', note: 'Road racing, some supermoto and velo' },
  { id: 'circuit', path: '/circuit', note: 'Car circuit: Curbstone, Skylimit, 2CV, BGDC' },
  { id: 'funfr', path: '/funfr', note: 'Fun Cup France' },
  { id: 'funbe', path: '/funbe', note: 'Fun Cup Belgium' },
  { id: 'supermoto', path: '/supermoto', note: 'Supermoto, offroad, velo, BEX' },
  ...Array.from({ length: 11 }, (_, i) => ({
    id: `enduro${i + 1}`,
    path: `/enduro/${i + 1}`,
    note: 'Enduro, one channel per rider category',
  })),
]
